import { defineComponent } from 'vue';
import dialogPolyfill from 'dialog-polyfill';
import { windowHasHTMLDialogElement } from '@/client/components/HTMLDialogElementCompatibility';
import { translateText, translateTextWithParams } from '@/client/directives/i18n';
import { answerDialog, dialogState } from '@/client/utils/dialogs';
export default defineComponent({
    name: 'AppDialog',
    data() {
        return {
            state: dialogState,
            /** What had the focus before the dialog opened; it gets it back afterwards. */
            returnFocus: undefined,
            /** Text size: larger when a phone shows the page zoomed out (the game page is 1260 px wide). */
            fontSize: '16px',
        };
    },
    computed: {
        request() {
            return this.state.current;
        },
        isConfirm() {
            return this.request?.kind === 'confirm';
        },
        danger() {
            return this.request?.danger === true;
        },
        title() {
            const title = this.request?.title;
            return title ? translateText(title) : '';
        },
        message() {
            const request = this.request;
            if (request === undefined) {
                return '';
            }
            if (request.translated === true) {
                return request.message;
            }
            if (request.params !== undefined && request.params.length > 0) {
                return translateTextWithParams(request.message, request.params.map(String));
            }
            return translateText(request.message);
        },
        okLabel() {
            return translateText(this.request?.okLabel ?? 'OK');
        },
        cancelLabel() {
            return translateText(this.request?.cancelLabel ?? 'Cancel');
        },
    },
    watch: {
        request(next, previous) {
            if (next === undefined) {
                this.close();
            }
            else if (next !== previous) {
                this.open();
            }
        },
    },
    methods: {
        dialog() {
            return this.$refs.dialog;
        },
        buttons() {
            return [this.$refs.ok, this.$refs.cancel].filter((b) => b instanceof HTMLButtonElement);
        },
        /**
         * On a phone the game page is laid out 1260 px wide and shown zoomed out, so 16 CSS px
         * would be tiny. Scale the dialog by how far the page is zoomed out.
         */
        readableFontSize() {
            // Only touch screens: a desktop browser window narrower than the screen is not zoomed out.
            if (window.matchMedia?.('(pointer: coarse)').matches !== true) {
                return '16px';
            }
            const visibleWidth = window.visualViewport?.width ?? window.innerWidth;
            const screenWidth = Math.min(window.screen?.width || visibleWidth, window.screen?.height || visibleWidth);
            const zoomedOut = screenWidth > 0 ? visibleWidth / screenWidth : 1;
            const factor = Math.min(3.5, Math.max(1, zoomedOut));
            return Math.round(16 * factor) + 'px';
        },
        open() {
            const dialog = this.dialog();
            this.fontSize = this.readableFontSize();
            if (!dialog.hasAttribute('open')) {
                const active = document.activeElement;
                this.returnFocus = active instanceof HTMLElement && active !== document.body ? active : undefined;
                if (typeof dialog.showModal === 'function') {
                    try {
                        dialog.showModal();
                    }
                    catch (e) {
                        // Not connected or already open in a way the attribute didn't show.
                        dialog.setAttribute('open', '');
                    }
                }
                else {
                    dialog.setAttribute('open', '');
                }
            }
            this.$nextTick(() => this.$refs.ok?.focus());
        },
        close() {
            const dialog = this.dialog();
            if (dialog.hasAttribute('open')) {
                if (typeof dialog.close === 'function') {
                    dialog.close();
                }
                else {
                    dialog.removeAttribute('open');
                }
            }
            const returnFocus = this.returnFocus;
            this.returnFocus = undefined;
            if (returnFocus?.isConnected) {
                returnFocus.focus();
            }
        },
        answer(ok) {
            if (this.request === undefined) {
                return;
            }
            answerDialog(ok);
        },
        onKeydown(event) {
            // Keys belong to the dialog: no page shortcut, popup or panel behind it reacts (Esc would close them too).
            event.stopPropagation();
            if (event.key === 'Escape') {
                // Native dialogs send 'cancel' too; answering twice is harmless (nothing is open by then).
                event.preventDefault();
                this.answer(false);
            }
            else if (event.key === 'Enter') {
                // A focused button handles Enter itself (Cancel stays Cancel). Anywhere else, Enter means OK.
                // A held-down key (from the click that opened this) doesn't count.
                const onButton = event.target instanceof HTMLButtonElement;
                if (event.repeat) {
                    event.preventDefault();
                }
                else if (!onButton) {
                    event.preventDefault();
                    this.answer(true);
                }
            }
            else if (event.key === 'Tab') {
                // Keep the focus inside the dialog.
                const buttons = this.buttons();
                if (buttons.length === 0) {
                    return;
                }
                const index = buttons.indexOf(document.activeElement);
                const nextIndex = event.shiftKey ?
                    (index <= 0 ? buttons.length - 1 : index - 1) :
                    (index < 0 || index >= buttons.length - 1 ? 0 : index + 1);
                event.preventDefault();
                buttons[nextIndex].focus();
            }
        },
    },
    mounted() {
        if (!windowHasHTMLDialogElement()) {
            dialogPolyfill.registerDialog(this.dialog());
        }
        if (this.request !== undefined) {
            this.open();
        }
    },
});
